import React, { useState, useEffect } from 'react';
import { 
  Plus, Search, Filter, Edit, Trash2, Save, X, Settings, MapPin, Euro, Percent,
  Building, Anchor, Globe, Calculator, FileText, Calendar, Users, Star, Clock,
  AlertTriangle, CheckCircle, ChevronDown, ChevronUp, Copy, Eye, Download,
  Zap, Shield, Award, Home, Car, Plane, Utensils, Coffee, Music, Heart,
  TrendingUp, BarChart3, PieChart, Activity, DollarSign, CreditCard
} from 'lucide-react';

const DestinationSettings = () => {
  const [activeTab, setActiveTab] = useState('destinations');
  const [selectedDestination, setSelectedDestination] = useState(null);
  const [selectedPropertyType, setSelectedPropertyType] = useState('villa');
  const [showNewDestinationModal, setShowNewDestinationModal] = useState(false);
  const [showNewTaxModal, setShowNewTaxModal] = useState(false);
  const [editingTax, setEditingTax] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // États pour nouvelle destination
  const [newDestination, setNewDestination] = useState({
    name: '',
    country: '',
    region: '',
    currency: 'EUR',
    timezone: 'Europe/Paris',
    isActive: true,
    coordinates: { lat: '', lng: '' },
    description: ''
  });

  // États pour nouvelle taxe
  const [newTax, setNewTax] = useState({
    name: '',
    type: 'percentage', // percentage, fixed, per_person, per_night
    rate: 0,
    fixedAmount: 0,
    applicationType: 'all', // all, villa_only, yacht_only, concierge_only
    calculationMethod: 'on_total', // on_total, on_subtotal, on_nights, on_persons
    isGeneral: true,
    isRequired: true,
    minAmount: 0,
    maxAmount: 0,
    exemptionRules: [],
    seasonalRates: [],
    description: ''
  });

  // Mock data pour les destinations
  const [destinations, setDestinations] = useState([
    {
      id: 'DEST-001',
      name: 'Saint-Barthélemy',
      country: 'France',
      region: 'Caraïbes',
      currency: 'EUR',
      timezone: 'America/Guadeloupe',
      isActive: true,
      coordinates: { lat: '17.9000', lng: '-62.8333' },
      description: 'Île de luxe dans les Caraïbes françaises',
      propertiesCount: { villas: 15, yachts: 8 },
      taxes: [
        {
          id: 'TAX-001',
          name: 'TVA Standard',
          type: 'percentage',
          rate: 8.5,
          applicationType: 'all',
          calculationMethod: 'on_subtotal',
          isGeneral: true,
          isRequired: true,
          description: 'TVA applicable aux prestations de services'
        },
        {
          id: 'TAX-002',
          name: 'Taxe de Séjour',
          type: 'per_person',
          fixedAmount: 2.5,
          applicationType: 'villa_only',
          calculationMethod: 'on_persons',
          isGeneral: false,
          isRequired: true,
          maxAmount: 50,
          description: 'Taxe locale par personne et par nuit'
        },
        {
          id: 'TAX-003',
          name: 'Frais de Port (Yachts)',
          type: 'percentage',
          rate: 3.0,
          applicationType: 'yacht_only',
          calculationMethod: 'on_total',
          isGeneral: false,
          isRequired: true,
          description: 'Frais portuaires pour les yachts'
        }
      ]
    },
    {
      id: 'DEST-002',
      name: 'Saint-Tropez',
      country: 'France',
      region: 'Provence-Alpes-Côte d\'Azur',
      currency: 'EUR',
      timezone: 'Europe/Paris',
      isActive: true,
      coordinates: { lat: '43.2677', lng: '6.6407' },
      description: 'Station balnéaire de luxe sur la Côte d\'Azur',
      propertiesCount: { villas: 12, yachts: 15 },
      taxes: [
        {
          id: 'TAX-004',
          name: 'TVA Standard',
          type: 'percentage',
          rate: 20.0,
          applicationType: 'all',
          calculationMethod: 'on_subtotal',
          isGeneral: true,
          isRequired: true,
          description: 'TVA française standard'
        },
        {
          id: 'TAX-005',
          name: 'Taxe de Séjour',
          type: 'fixed',
          fixedAmount: 4.0,
          applicationType: 'villa_only',
          calculationMethod: 'on_nights',
          isGeneral: false,
          isRequired: true,
          description: 'Taxe municipale fixe par nuit'
        },
        {
          id: 'TAX-006',
          name: 'Frais de Marina',
          type: 'per_night',
          fixedAmount: 150,
          applicationType: 'yacht_only',
          calculationMethod: 'on_nights',
          isGeneral: false,
          isRequired: false,
          description: 'Frais de marina par nuit'
        }
      ]
    },
    {
      id: 'DEST-003',
      name: 'Mykonos',
      country: 'Grèce',
      region: 'Cyclades',
      currency: 'EUR',
      timezone: 'Europe/Athens',
      isActive: true,
      coordinates: { lat: '37.4467', lng: '25.3289' },
      description: 'Île cosmopolite des Cyclades',
      propertiesCount: { villas: 8, yachts: 12 },
      taxes: [
        {
          id: 'TAX-007',
          name: 'TVA Grèce',
          type: 'percentage',
          rate: 24.0,
          applicationType: 'all',
          calculationMethod: 'on_subtotal',
          isGeneral: true,
          isRequired: true,
          description: 'TVA grecque standard'
        },
        {
          id: 'TAX-008',
          name: 'Taxe Climatique',
          type: 'per_person',
          fixedAmount: 1.5,
          applicationType: 'all',
          calculationMethod: 'on_persons',
          isGeneral: false,
          isRequired: true,
          maxAmount: 30,
          seasonalRates: [
            { period: 'Été (Juin-Septembre)', rate: 3.0 },
            { period: 'Hiver (Octobre-Mai)', rate: 1.5 }
          ],
          description: 'Taxe environnementale grecque'
        }
      ]
    }
  ]);

  // Types de taxes disponibles
  const taxTypes = [
    { value: 'percentage', label: 'Pourcentage (%)', icon: Percent },
    { value: 'fixed', label: 'Montant fixe', icon: Euro },
    { value: 'per_person', label: 'Par personne', icon: Users },
    { value: 'per_night', label: 'Par nuit', icon: Calendar }
  ];

  // Types d'application
  const applicationTypes = [
    { value: 'all', label: 'Toutes prestations', icon: Globe },
    { value: 'villa_only', label: 'Villas uniquement', icon: Building },
    { value: 'yacht_only', label: 'Yachts uniquement', icon: Anchor },
    { value: 'concierge_only', label: 'Services conciergerie', icon: Star }
  ];

  // Méthodes de calcul
  const calculationMethods = [
    { value: 'on_total', label: 'Sur le montant total TTC' },
    { value: 'on_subtotal', label: 'Sur le montant HT' },
    { value: 'on_nights', label: 'Sur le nombre de nuits' },
    { value: 'on_persons', label: 'Sur le nombre de personnes' }
  ];

  // Fonctions utilitaires
  const getTaxTypeLabel = (type) => {
    const taxType = taxTypes.find(t => t.value === type);
    return taxType ? taxType.label : type;
  };

  const getApplicationTypeLabel = (type) => {
    const appType = applicationTypes.find(t => t.value === type);
    return appType ? appType.label : type;
  };

  const formatTaxDisplay = (tax) => {
    if (tax.type === 'percentage') {
      return `${tax.rate}%`;
    } else if (tax.type === 'fixed') {
      return `€${tax.fixedAmount}`;
    } else if (tax.type === 'per_person') {
      return `€${tax.fixedAmount}/pers`;
    } else if (tax.type === 'per_night') {
      return `€${tax.fixedAmount}/nuit`;
    }
    return 'N/A';
  };

  // Gestion des destinations
  const handleCreateDestination = () => {
    const destinationData = {
      id: `DEST-${String(destinations.length + 1).padStart(3, '0')}`,
      ...newDestination,
      propertiesCount: { villas: 0, yachts: 0 },
      taxes: []
    };

    setDestinations([...destinations, destinationData]);
    setShowNewDestinationModal(false);
    setNewDestination({
      name: '',
      country: '',
      region: '',
      currency: 'EUR',
      timezone: 'Europe/Paris',
      isActive: true,
      coordinates: { lat: '', lng: '' },
      description: ''
    });
  };

  // Gestion des taxes
  const handleCreateTax = () => {
    if (!selectedDestination) return;

    const taxData = {
      id: `TAX-${String(Date.now()).slice(-3)}`,
      ...newTax
    };

    const updatedDestinations = destinations.map(dest => {
      if (dest.id === selectedDestination.id) {
        return {
          ...dest,
          taxes: [...dest.taxes, taxData]
        };
      }
      return dest;
    });

    setDestinations(updatedDestinations);
    setSelectedDestination({
      ...selectedDestination,
      taxes: [...selectedDestination.taxes, taxData]
    });
    setShowNewTaxModal(false);
    setNewTax({
      name: '',
      type: 'percentage',
      rate: 0,
      fixedAmount: 0,
      applicationType: 'all',
      calculationMethod: 'on_total',
      isGeneral: true,
      isRequired: true,
      minAmount: 0,
      maxAmount: 0,
      exemptionRules: [],
      seasonalRates: [],
      description: ''
    });
  };

  const handleDeleteTax = (taxId) => {
    const updatedDestinations = destinations.map(dest => {
      if (dest.id === selectedDestination.id) {
        return {
          ...dest,
          taxes: dest.taxes.filter(tax => tax.id !== taxId)
        };
      }
      return dest;
    });

    setDestinations(updatedDestinations);
    setSelectedDestination({
      ...selectedDestination,
      taxes: selectedDestination.taxes.filter(tax => tax.id !== taxId)
    });
  };

  // Filtrage des destinations
  const filteredDestinations = destinations.filter(dest =>
    dest.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    dest.country.toLowerCase().includes(searchTerm.toLowerCase()) ||
    dest.region.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="destination-settings min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Paramétrage des Destinations</h1>
            <p className="text-gray-600 mt-2">Configuration des destinations, taxes et frais par localisation</p>
          </div>
          <div className="flex items-center space-x-3 mt-4 lg:mt-0">
            <button 
              onClick={() => alert('Export configuration...')}
              className="flex items-center space-x-2 px-4 py-2 text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50"
            >
              <Download size={18} />
              <span>Export Config</span>
            </button>
            <button 
              onClick={() => setShowNewDestinationModal(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus size={18} />
              <span>Nouvelle Destination</span>
            </button>
          </div>
        </div>

        {/* Statistiques globales */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <MapPin className="text-blue-600" size={24} />
              <span className="text-xs text-blue-600">Total</span>
            </div>
            <div className="text-2xl font-bold text-blue-600">{destinations.length}</div>
            <div className="text-sm text-gray-600">Destinations</div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <Building className="text-green-600" size={24} />
              <span className="text-xs text-green-600">Villas</span>
            </div>
            <div className="text-2xl font-bold text-green-600">
              {destinations.reduce((sum, dest) => sum + dest.propertiesCount.villas, 0)}
            </div>
            <div className="text-sm text-gray-600">Propriétés</div>
          </div>

          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <Anchor className="text-purple-600" size={24} />
              <span className="text-xs text-purple-600">Yachts</span>
            </div>
            <div className="text-2xl font-bold text-purple-600">
              {destinations.reduce((sum, dest) => sum + dest.propertiesCount.yachts, 0)}
            </div>
            <div className="text-sm text-gray-600">Charter</div>
          </div>

          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <Calculator className="text-orange-600" size={24} />
              <span className="text-xs text-orange-600">Taxes</span>
            </div>
            <div className="text-2xl font-bold text-orange-600">
              {destinations.reduce((sum, dest) => sum + dest.taxes.length, 0)}
            </div>
            <div className="text-sm text-gray-600">Configurées</div>
          </div>
        </div>
      </div>

      <div className="flex">
        {/* Liste des destinations */}
        <div className="w-1/3 bg-white border-r border-gray-200 p-6">
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Rechercher une destination..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div className="space-y-3">
            {filteredDestinations.map((destination) => (
              <div
                key={destination.id}
                onClick={() => setSelectedDestination(destination)}
                className={`p-4 rounded-lg cursor-pointer transition-all duration-200 ${
                  selectedDestination?.id === destination.id
                    ? 'bg-blue-100 border-2 border-blue-500'
                    : 'bg-gray-50 border border-gray-200 hover:bg-gray-100'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-gray-800">{destination.name}</h3>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    destination.isActive 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {destination.isActive ? 'Actif' : 'Inactif'}
                  </span>
                </div>
                <div className="text-sm text-gray-600 mb-2">
                  {destination.region}, {destination.country}
                </div>
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <div className="flex items-center space-x-4">
                    <span className="flex items-center">
                      <Building size={12} className="mr-1" />
                      {destination.propertiesCount.villas} villas
                    </span>
                    <span className="flex items-center">
                      <Anchor size={12} className="mr-1" />
                      {destination.propertiesCount.yachts} yachts
                    </span>
                  </div>
                  <span className="flex items-center">
                    <Calculator size={12} className="mr-1" />
                    {destination.taxes.length} taxes
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Détails de la destination sélectionnée */}
        <div className="flex-1 p-6">
          {selectedDestination ? (
            <div className="space-y-6">
              {/* En-tête destination */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-800">{selectedDestination.name}</h2>
                    <p className="text-gray-600">{selectedDestination.region}, {selectedDestination.country}</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button 
                      onClick={() => alert('Édition destination...')}
                      className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg"
                    >
                      <Edit size={20} />
                    </button>
                    <button 
                      onClick={() => alert('Copier destination...')}
                      className="p-2 text-green-600 hover:bg-green-100 rounded-lg"
                    >
                      <Copy size={20} />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <span className="text-sm text-gray-600">Devise</span>
                    <div className="font-semibold">{selectedDestination.currency}</div>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">Fuseau horaire</span>
                    <div className="font-semibold">{selectedDestination.timezone}</div>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">Coordonnées</span>
                    <div className="font-semibold text-xs">
                      {selectedDestination.coordinates.lat}, {selectedDestination.coordinates.lng}
                    </div>
                  </div>
                </div>

                {selectedDestination.description && (
                  <div className="mt-4">
                    <span className="text-sm text-gray-600">Description</span>
                    <p className="text-gray-800">{selectedDestination.description}</p>
                  </div>
                )}
              </div>

              {/* Onglets Villa/Yacht */}
              <div className="bg-white rounded-lg border border-gray-200">
                <div className="border-b border-gray-200 px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex space-x-1">
                      <button
                        onClick={() => setSelectedPropertyType('villa')}
                        className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium ${
                          selectedPropertyType === 'villa'
                            ? 'bg-blue-100 text-blue-700'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <Building size={16} />
                        <span>Configuration Villas ({selectedDestination.taxes.filter(t => t.applicationType === 'villa_only' || t.applicationType === 'all').length})</span>
                      </button>
                      <button
                        onClick={() => setSelectedPropertyType('yacht')}
                        className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium ${
                          selectedPropertyType === 'yacht'
                            ? 'bg-purple-100 text-purple-700'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <Anchor size={16} />
                        <span>Configuration Yachts ({selectedDestination.taxes.filter(t => t.applicationType === 'yacht_only' || t.applicationType === 'all').length})</span>
                      </button>
                    </div>
                    <button
                      onClick={() => setShowNewTaxModal(true)}
                      className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                    >
                      <Plus size={16} />
                      <span>Nouvelle Taxe</span>
                    </button>
                  </div>
                </div>

                <div className="p-6">
                  {/* Liste des taxes pour le type sélectionné */}
                  <div className="space-y-4">
                    {selectedDestination.taxes
                      .filter(tax => 
                        tax.applicationType === 'all' || 
                        tax.applicationType === `${selectedPropertyType}_only` ||
                        (selectedPropertyType === 'villa' && tax.applicationType.includes('villa')) ||
                        (selectedPropertyType === 'yacht' && tax.applicationType.includes('yacht'))
                      )
                      .map((tax) => (
                        <div key={tax.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center space-x-3">
                              <div className={`p-2 rounded-lg ${
                                tax.isGeneral ? 'bg-blue-100' : 'bg-orange-100'
                              }`}>
                                {tax.type === 'percentage' && <Percent className={tax.isGeneral ? 'text-blue-600' : 'text-orange-600'} size={16} />}
                                {tax.type === 'fixed' && <Euro className={tax.isGeneral ? 'text-blue-600' : 'text-orange-600'} size={16} />}
                                {tax.type === 'per_person' && <Users className={tax.isGeneral ? 'text-blue-600' : 'text-orange-600'} size={16} />}
                                {tax.type === 'per_night' && <Calendar className={tax.isGeneral ? 'text-blue-600' : 'text-orange-600'} size={16} />}
                              </div>
                              <div>
                                <h4 className="font-semibold text-gray-800">{tax.name}</h4>
                                <p className="text-sm text-gray-600">{getApplicationTypeLabel(tax.applicationType)}</p>
                              </div>
                            </div>
                            <div className="flex items-center space-x-3">
                              <div className="text-right">
                                <div className="text-lg font-bold text-gray-800">
                                  {formatTaxDisplay(tax)}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {getTaxTypeLabel(tax.type)}
                                </div>
                              </div>
                              <div className="flex items-center space-x-1">
                                <button
                                  onClick={() => setEditingTax(tax)}
                                  className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                                >
                                  <Edit size={14} />
                                </button>
                                <button
                                  onClick={() => handleDeleteTax(tax.id)}
                                  className="p-1 text-red-600 hover:bg-red-100 rounded"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                            <div>
                              <span className="text-gray-600">Méthode de calcul:</span>
                              <div className="font-medium">
                                {calculationMethods.find(m => m.value === tax.calculationMethod)?.label}
                              </div>
                            </div>
                            <div>
                              <span className="text-gray-600">Obligatoire:</span>
                              <div className="font-medium">
                                {tax.isRequired ? (
                                  <span className="text-green-600">Oui</span>
                                ) : (
                                  <span className="text-orange-600">Non</span>
                                )}
                              </div>
                            </div>
                            <div>
                              <span className="text-gray-600">Type:</span>
                              <div className="font-medium">
                                {tax.isGeneral ? (
                                  <span className="text-blue-600">Généralisée</span>
                                ) : (
                                  <span className="text-orange-600">Spécifique</span>
                                )}
                              </div>
                            </div>
                          </div>

                          {tax.description && (
                            <div className="mt-3 text-sm text-gray-700 bg-white p-3 rounded border">
                              {tax.description}
                            </div>
                          )}

                          {tax.seasonalRates && tax.seasonalRates.length > 0 && (
                            <div className="mt-3">
                              <span className="text-sm font-medium text-gray-700">Taux saisonniers:</span>
                              <div className="mt-2 space-y-1">
                                {tax.seasonalRates.map((seasonal, index) => (
                                  <div key={index} className="flex justify-between items-center text-sm bg-white p-2 rounded border">
                                    <span className="text-gray-600">{seasonal.period}</span>
                                    <span className="font-medium">€{seasonal.rate}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {(tax.minAmount > 0 || tax.maxAmount > 0) && (
                            <div className="mt-3 flex space-x-4 text-sm">
                              {tax.minAmount > 0 && (
                                <div>
                                  <span className="text-gray-600">Minimum:</span>
                                  <span className="font-medium ml-1">€{tax.minAmount}</span>
                                </div>
                              )}
                              {tax.maxAmount > 0 && (
                                <div>
                                  <span className="text-gray-600">Maximum:</span>
                                  <span className="font-medium ml-1">€{tax.maxAmount}</span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}

                    {selectedDestination.taxes.filter(tax => 
                      tax.applicationType === 'all' || 
                      tax.applicationType === `${selectedPropertyType}_only`
                    ).length === 0 && (
                      <div className="text-center py-12 text-gray-500">
                        <Calculator size={48} className="mx-auto mb-4 text-gray-400" />
                        <p>Aucune taxe configurée pour {selectedPropertyType === 'villa' ? 'les villas' : 'les yachts'} dans cette destination</p>
                        <button
                          onClick={() => setShowNewTaxModal(true)}
                          className="mt-4 text-blue-600 hover:text-blue-800"
                        >
                          Ajouter la première taxe
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <MapPin size={48} className="mx-auto mb-4 text-gray-400" />
              <p>Sélectionnez une destination pour voir sa configuration</p>
            </div>
          )}
        </div>
      </div>

      {/* Modal Nouvelle Destination */}
      {showNewDestinationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-gray-800">Nouvelle Destination</h3>
              <button
                onClick={() => setShowNewDestinationModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nom de la destination</label>
                  <input
                    type="text"
                    value={newDestination.name}
                    onChange={(e) => setNewDestination({...newDestination, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Saint-Barthélemy"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Pays</label>
                  <input
                    type="text"
                    value={newDestination.country}
                    onChange={(e) => setNewDestination({...newDestination, country: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="France"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Région</label>
                  <input
                    type="text"
                    value={newDestination.region}
                    onChange={(e) => setNewDestination({...newDestination, region: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Caraïbes"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Devise</label>
                  <select
                    value={newDestination.currency}
                    onChange={(e) => setNewDestination({...newDestination, currency: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="EUR">EUR - Euro</option>
                    <option value="USD">USD - Dollar US</option>
                    <option value="GBP">GBP - Livre Sterling</option>
                    <option value="CHF">CHF - Franc Suisse</option>
                  </select>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Fuseau horaire</label>
                  <select
                    value={newDestination.timezone}
                    onChange={(e) => setNewDestination({...newDestination, timezone: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="Europe/Paris">Europe/Paris</option>
                    <option value="Europe/London">Europe/London</option>
                    <option value="America/New_York">America/New_York</option>
                    <option value="America/Guadeloupe">America/Guadeloupe</option>
                    <option value="Europe/Athens">Europe/Athens</option>
                    <option value="Europe/Rome">Europe/Rome</option>
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Latitude</label>
                    <input
                      type="text"
                      value={newDestination.coordinates.lat}
                      onChange={(e) => setNewDestination({
                        ...newDestination, 
                        coordinates: {...newDestination.coordinates, lat: e.target.value}
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="17.9000"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Longitude</label>
                    <input
                      type="text"
                      value={newDestination.coordinates.lng}
                      onChange={(e) => setNewDestination({
                        ...newDestination, 
                        coordinates: {...newDestination.coordinates, lng: e.target.value}
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="-62.8333"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    value={newDestination.description}
                    onChange={(e) => setNewDestination({...newDestination, description: e.target.value})}
                    rows="4"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Description de la destination..."
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={newDestination.isActive}
                    onChange={(e) => setNewDestination({...newDestination, isActive: e.target.checked})}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="isActive" className="ml-2 block text-sm text-gray-700">
                    Destination active
                  </label>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowNewDestinationModal(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={handleCreateDestination}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Créer la Destination
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Nouvelle Taxe */}
      {showNewTaxModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-gray-800">
                Nouvelle Taxe - {selectedDestination?.name}
              </h3>
              <button
                onClick={() => setShowNewTaxModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Partie gauche */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nom de la taxe</label>
                  <input
                    type="text"
                    value={newTax.name}
                    onChange={(e) => setNewTax({...newTax, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="TVA Standard"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Type de taxe</label>
                  <select
                    value={newTax.type}
                    onChange={(e) => setNewTax({...newTax, type: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {taxTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>

                {newTax.type === 'percentage' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Taux (%)</label>
                    <input
                      type="number"
                      step="0.1"
                      value={newTax.rate}
                      onChange={(e) => setNewTax({...newTax, rate: parseFloat(e.target.value) || 0})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="20.0"
                    />
                  </div>
                )}

                {newTax.type !== 'percentage' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Montant (€)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={newTax.fixedAmount}
                      onChange={(e) => setNewTax({...newTax, fixedAmount: parseFloat(e.target.value) || 0})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="2.50"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Application</label>
                  <select
                    value={newTax.applicationType}
                    onChange={(e) => setNewTax({...newTax, applicationType: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {applicationTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Méthode de calcul</label>
                  <select
                    value={newTax.calculationMethod}
                    onChange={(e) => setNewTax({...newTax, calculationMethod: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {calculationMethods.map(method => (
                      <option key={method.value} value={method.value}>{method.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Partie droite */}
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Montant minimum (€)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={newTax.minAmount}
                      onChange={(e) => setNewTax({...newTax, minAmount: parseFloat(e.target.value) || 0})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Montant maximum (€)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={newTax.maxAmount}
                      onChange={(e) => setNewTax({...newTax, maxAmount: parseFloat(e.target.value) || 0})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="0"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    value={newTax.description}
                    onChange={(e) => setNewTax({...newTax, description: e.target.value})}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Description de la taxe..."
                  />
                </div>

                <div className="space-y-3">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="isGeneral"
                      checked={newTax.isGeneral}
                      onChange={(e) => setNewTax({...newTax, isGeneral: e.target.checked})}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="isGeneral" className="ml-2 block text-sm text-gray-700">
                      Taxe généralisée (appliquée automatiquement)
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="isRequired"
                      checked={newTax.isRequired}
                      onChange={(e) => setNewTax({...newTax, isRequired: e.target.checked})}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="isRequired" className="ml-2 block text-sm text-gray-700">
                      Taxe obligatoire
                    </label>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-800 mb-2">Aperçu du calcul</h4>
                  <div className="text-sm text-gray-600">
                    {newTax.type === 'percentage' && (
                      <p>Cette taxe sera calculée à {newTax.rate}% {newTax.calculationMethod === 'on_total' ? 'du montant total TTC' : 'du montant HT'}</p>
                    )}
                    {newTax.type === 'fixed' && (
                      <p>Cette taxe sera de {newTax.fixedAmount}€ fixe par réservation</p>
                    )}
                    {newTax.type === 'per_person' && (
                      <p>Cette taxe sera de {newTax.fixedAmount}€ par personne</p>
                    )}
                    {newTax.type === 'per_night' && (
                      <p>Cette taxe sera de {newTax.fixedAmount}€ par nuit</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowNewTaxModal(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={handleCreateTax}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Créer la Taxe
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DestinationSettings;